import { Component, ElementRef, inject, OnInit, signal, ViewChild } from '@angular/core';
import { TesseractService } from '../services/tesseract.service';
import { QrCodeService } from '../services/qr-html.service';
import { QuaggaService } from '../services/quaqqa.service';
import { LicensePlateRecognition } from '../services/licensePlateRecognition';
import { licensePlateRectangle, qrCodeRectangle } from '../constants';
import { calculateOverlayPosition, calculateOverlaySize } from '../crop.utils';
import { ZxingService } from '../services/zxing.service';
import { ProcessedImage } from '../services/processed-image.interface';

@Component({
  selector: 'app-camera-preview',
  templateUrl: 'scanner.html',
  styleUrls: ['./scanner.css'],
  standalone: true,
})
export class CameraPreviewComponent implements OnInit {
  licensePlateRectangle = licensePlateRectangle;
  qrCodeRectangle = qrCodeRectangle;
  @ViewChild('video', { static: true }) videoRef!: ElementRef<HTMLVideoElement>;
  @ViewChild('canvas', { static: true })
  canvasRef!: ElementRef<HTMLCanvasElement>;

  availableCameras: Array<{ id: string; name: string }> = [];

  mode: 'takePicture' | 'processingPicture' | 'showResult' = 'takePicture';

  takenImagePng: string | null = null;

  results: Array<ProcessedImage & { lib: string }> = [];

  scanType: 'qr' | 'plate' = 'plate';

  private tesseractService = inject(TesseractService);
  private qrCodeService = inject(QrCodeService);
  private quaggaService = inject(QuaggaService);
  private licensePlateRecognition = inject(LicensePlateRecognition);
  private zxingService = inject(ZxingService);

  overlayTop = signal(0);
  overlayLeft = signal(0);
  overlayHeight = signal(0);
  overlayWidth = signal(0);

  ngOnInit() {
    const cameraId = localStorage.getItem('cameraId') ?? undefined;

    this.startCamera(cameraId);

    const cameraPreview = document.getElementById('cameraPreview');
    if (cameraPreview) {
      const observer = new ResizeObserver(() => {
        this.recalcOverlay();
      });
      observer.observe(cameraPreview);
    }

    this.listCameras();
  }

  private recalcOverlay() {
    const rect = this.videoRef.nativeElement.getBoundingClientRect();

    const x = this.scanType === 'plate' ? licensePlateRectangle.x : qrCodeRectangle.x;
    const y = this.scanType === 'plate' ? licensePlateRectangle.y : qrCodeRectangle.y;

    this.overlayTop.set(calculateOverlayPosition(rect.height, y));
    this.overlayHeight.set(calculateOverlaySize(rect.height, y));

    this.overlayLeft.set(calculateOverlayPosition(rect.width, x));
    this.overlayWidth.set(calculateOverlaySize(rect.width, x));
  }

  changeType(type: any) {
    this.scanType = type;
    this.recalcOverlay();
  }

  private listCameras() {
    navigator.mediaDevices
      .enumerateDevices()
      .then(devices => {
        const videoInputDevices = devices.filter(device => device.kind === 'videoinput');
        videoInputDevices.forEach(device => {
          this.availableCameras.push({
            id: device.deviceId,
            name: device.label,
          });
          console.log(`Device ID: ${device.deviceId}, Label: ${device.label}`);
        });
      })
      .catch(err => {
        console.error('Error listing cameras: ' + err);
      });
  }

  enlarge(imageData: string) {
    const modal = document.getElementById('myModal');
    const modalImg = document.getElementById('modal-image') as HTMLImageElement;
    const img = event.target;
    modal.style.display = 'block';
    modalImg.src = imageData;
  }

  closeModal() {
    const modal = document.getElementById('myModal');
    modal.style.display = 'none';
  }

  startCamera(cameraId?: string) {
    const video = this.videoRef.nativeElement;

    (video.srcObject as MediaStream)?.getTracks().forEach(track => track.stop());

    if (!cameraId) {
      navigator.mediaDevices
        .getUserMedia({
          video: {
            facingMode: { ideal: 'environment' },
          },
        })
        .then(stream => {
          video.srcObject = stream;
          video.play();
        })
        .catch(err => {
          console.error('An error occurred: ' + err);
        });
      return;
    }

    localStorage.setItem('cameraId', cameraId);

    navigator.mediaDevices
      .getUserMedia({
        video: {
          deviceId: { exact: cameraId },
        },
      })
      .then(stream => {
        video.srcObject = stream;
        video.play();
      })
      .catch(err => {
        console.error('An error occurred: ' + err);
      });
  }

  async takePicture() {
    const canvas = this.canvasRef.nativeElement;
    const context = canvas.getContext('2d');
    const video = this.videoRef.nativeElement;

    if (context) {
      const x = this.scanType === 'plate' ? licensePlateRectangle.x : qrCodeRectangle.x;
      const y = this.scanType === 'plate' ? licensePlateRectangle.y : qrCodeRectangle.y;

      const overlayTop = calculateOverlayPosition(video.videoHeight, y);
      const overlayLeft = calculateOverlayPosition(video.videoWidth, x);
      const overlayWidth = calculateOverlaySize(video.videoWidth, x);
      const overlayHeight = calculateOverlaySize(video.videoHeight, y);

      canvas.width = overlayWidth;
      canvas.height = overlayHeight;

      // crop to size
      context.drawImage(video, overlayLeft, overlayTop, overlayWidth, overlayHeight, 0, 0, canvas.width, canvas.height);
      const blob = await this.getBlobFromCanvas();
      this.takenImagePng = canvas.toDataURL('image/png');
      this.processPicture(blob, canvas);
    }
  }

  private async getBlobFromCanvas(): Promise<Blob | null> {
    const canvas = this.canvasRef.nativeElement;

    return new Promise<Blob | null>(resolve => {
      canvas.toBlob(blob => {
        resolve(blob);
      }, 'image/jpeg');
    });
  }

  print() {
    window.addEventListener('beforeprint', () => {
      document.title = 'Scan Result ' + Date.now();
    });
    window.print();
  }

  async processPicture(imageBlob: Blob | null, canvas: HTMLCanvasElement) {
    this.mode = 'processingPicture';
    this.results = [];

    const cvResult = this.licensePlateRecognition.processImage(canvas);

    this.results.push({
      lib: 'License Plate Detection',
      result: 'Potential Plates: ' + cvResult.foundPlates,
      imageResult: cvResult.base64Image,
      imageUsed: this.takenImagePng,
      time: cvResult.time,
    });

    if (this.takenImagePng && imageBlob) {
      const tesseractResult = await this.tesseractService.processImage(cvResult.base64ImageForTesseract);
      const tesseractGrayscaleResult = await this.tesseractService.processImage(
        cvResult.base64ImageForTesseractGrayscale
      );
      const qrCodeResult = await this.qrCodeService.processImage(imageBlob);
      const quaqqaResult = await this.quaggaService.processImage(this.takenImagePng);
      const zxingResult = await this.zxingService.processImage(canvas, canvas.width, canvas.height);

      this.results.push({
        lib: "Tesseract (Otsu's thresholding)",
        result: tesseractResult.result,
        time: tesseractResult.time,
        imageUsed: cvResult.base64ImageForTesseract,
      });

      this.results.push({
        lib: 'Tesseract (Grayscale)',
        result: tesseractGrayscaleResult.result,
        time: tesseractGrayscaleResult.time,
        imageUsed: tesseractGrayscaleResult.imageUsed,
      });
      this.results.push({
        lib: 'Html5 QRCode',
        result: qrCodeResult.result,
        imageUsed: this.takenImagePng,
        time: qrCodeResult.time,
      });
      this.results.push({
        lib: 'Quagga',
        result: quaqqaResult.result,
        imageUsed: this.takenImagePng,
        time: quaqqaResult.time,
      });
      this.results.push({
        lib: 'Zxing',
        result: zxingResult.result,
        imageUsed: this.takenImagePng,
        time: zxingResult.time,
      });
    }

    this.mode = 'showResult';
  }
}
